import type { Monaco } from '@monaco-editor/react';
import type * as MonacoEditor from 'monaco-editor';

import type { VantageMetadata } from '../../../types';

const SQL_KEYWORDS = [
    'SELECT',
    'FROM',
    'WHERE',
    'JOIN',
    'INNER JOIN',
    'LEFT JOIN',
    'RIGHT JOIN',
    'FULL JOIN',
    'ON',
    'AND',
    'OR',
    'GROUP BY',
    'ORDER BY',
    'HAVING',
    'LIMIT',
    'COUNT',
    'SUM',
    'AVG',
    'MIN',
    'MAX',
    'AS',
    'DESC',
    'ASC',
    'DISTINCT',
    'CASE',
    'WHEN',
    'THEN',
    'ELSE',
    'END',
    'WITH',
    'OFFSET',
    'ALL',
    'BETWEEN',
    'BY',
    'EXISTS',
    'NULL',
    'NOT',
];

const SQL_TRIGGER_CHARACTERS = [
    ' ',
    '.',
    ',',
    '{',
    '}',
    '_',
    ...'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split(''),
];

type CompletionScope = 'TABLE' | 'COLUMN' | 'TABLE_DOT' | 'VARIABLE' | 'DEFAULT';

const normalizeSQL = (text: string) => text.replace(/\s+/g, ' ').toUpperCase();

const detectScope = (textBeforeCursor: string): CompletionScope => {
    const text = normalizeSQL(textBeforeCursor);
    const lastVariableOpen = textBeforeCursor.lastIndexOf('{{');
    const lastVariableClose = textBeforeCursor.lastIndexOf('}}');

    if (lastVariableOpen > lastVariableClose) {return 'VARIABLE';}

    if (/\b\w+\.$/.test(textBeforeCursor)) {return 'TABLE_DOT';}
    if (/\b(FROM|JOIN)\s+[\w"]*$/.test(text)) {return 'TABLE';}
    if (/\b(SELECT|WHERE|AND|OR|ON|HAVING|GROUP BY|ORDER BY)\b/.test(text)) {return 'COLUMN';}

    return 'DEFAULT';
};

const getTextBeforeCursor = (model: MonacoEditor.editor.ITextModel, position: MonacoEditor.Position) =>
    model.getValueInRange({
        startLineNumber: 1,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
    });

const getDynamicVariableRange = (
    model: MonacoEditor.editor.ITextModel,
    position: MonacoEditor.Position,
    word: MonacoEditor.editor.IWordAtPosition,
) => {
    const lineContent = model.getLineContent(position.lineNumber);
    const linePrefix = lineContent.slice(0, position.column - 1);
    const lineSuffix = lineContent.slice(position.column - 1);
    const variableOpenIndex = linePrefix.lastIndexOf('{{');
    const existingCloseMatch = lineSuffix.match(/^\s*}}/);

    if (variableOpenIndex === -1) {
        return undefined;
    }

    return {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: variableOpenIndex + 3,
        endColumn: existingCloseMatch
            ? Math.max(word.endColumn, position.column + existingCloseMatch[0].length)
            : word.endColumn,
    } satisfies MonacoEditor.IRange;
};

const getDynamicVariableSnippetRange = (
    model: MonacoEditor.editor.ITextModel,
    position: MonacoEditor.Position,
    fallbackRange: MonacoEditor.IRange,
) => {
    const linePrefix = model.getLineContent(position.lineNumber).slice(0, position.column - 1);

    if (linePrefix.endsWith('{') && !linePrefix.endsWith('{{')) {
        return {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: Math.max(1, position.column - 1),
            endColumn: position.column,
        } satisfies MonacoEditor.IRange;
    }

    return fallbackRange;
};

const normalizeIdentifier = (identifier: string) => identifier.replace(/^"|"$/g, '').toLowerCase();

const getQueryAliases = (sql: string, metadata: VantageMetadata) => {
    const aliases = new Map<string, string>();
    const tableNames = new Set(metadata.tables.map((table) => table.name.toLowerCase()));
    const pattern = /\b(?:from|join)\s+("?[\w]+"?)(?:\s+(?:as\s+)?("?[\w]+"?))?/gi;

    let match: RegExpExecArray | null;

    while ((match = pattern.exec(sql))) {
        const tableName = normalizeIdentifier(match[1]);
        const alias = match[2] ? normalizeIdentifier(match[2]) : tableName;

        if (tableNames.has(tableName)) {
            aliases.set(alias, tableName);
        }
    }

    return aliases;
};

const getReferencedTables = (sql: string, metadata: VantageMetadata) => {
    const aliases = getQueryAliases(sql, metadata);
    const tableNames = [...new Set(aliases.values())];

    return tableNames.length
        ? metadata.tables.filter((table) => tableNames.includes(table.name.toLowerCase()))
        : metadata.tables;
};

export const registerVantageCompletionProvider = (monacoInstance: Monaco, getMetadata: () => VantageMetadata) =>
    monacoInstance.languages.registerCompletionItemProvider('sql', {
        triggerCharacters: SQL_TRIGGER_CHARACTERS,
        provideCompletionItems(model, position) {
            const metadata = getMetadata();
            const word = model.getWordUntilPosition(position);
            const range: MonacoEditor.IRange = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn,
            };
            const textBeforeCursor = getTextBeforeCursor(model, position);
            const scope = detectScope(textBeforeCursor);
            const referencedTables = getReferencedTables(model.getValue(), metadata);
            const aliases = getQueryAliases(model.getValue(), metadata);
            const suggestions: MonacoEditor.languages.CompletionItem[] = [];

            if (scope === 'VARIABLE') {
                const variableRange = getDynamicVariableRange(model, position, word) ?? range;

                metadata.variables.forEach((variable) => {
                    suggestions.push({
                        label: {
                            label: variable.key,
                            description: variable.type,
                        },
                        kind: monacoInstance.languages.CompletionItemKind.Variable,
                        insertText: ` ${variable.key} }}`,
                        documentation: `Dynamic variable: ${variable.key} (${variable.type})`,
                        sortText: `10_variable_${variable.key}`,
                        range: variableRange,
                    });
                });

                return { suggestions, incomplete: false };
            }

            SQL_KEYWORDS.forEach((keyword, index) => {
                suggestions.push({
                    label: {
                        label: keyword,
                        description: 'SQL Keyword',
                    },
                    kind: monacoInstance.languages.CompletionItemKind.Keyword,
                    insertText: keyword,
                    sortText: `00_keyword_${String(index).padStart(2, '0')}_${keyword}`,
                    range,
                });
            });

            suggestions.push({
                label: {
                    label: 'SELECT ... FROM ...',
                    description: 'Template',
                },
                kind: monacoInstance.languages.CompletionItemKind.Snippet,
                insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                insertText: ['SELECT ${1:*}', 'FROM ${2:table}', 'WHERE ${3:condition}', 'LIMIT ${4:100};'].join('\n'),
                sortText: '99_template_select_from',
                range,
            });

            const dynamicVariableSnippetRange = getDynamicVariableSnippetRange(model, position, range);

            metadata.variables.forEach((variable) => {
                suggestions.push({
                    label: {
                        label: `{{ ${variable.key} }}`,
                        description: variable.type,
                    },
                    kind: monacoInstance.languages.CompletionItemKind.Variable,
                    insertText: `{{ ${variable.key} }}`,
                    documentation: `Dynamic variable: ${variable.key} (${variable.type})`,
                    sortText: `10_variable_${variable.key}`,
                    range: dynamicVariableSnippetRange,
                });
            });

            if (scope === 'TABLE' || scope === 'DEFAULT') {
                metadata.tables.forEach((table) => {
                    suggestions.push({
                        label: { label: table.name, description: 'table' },
                        kind: monacoInstance.languages.CompletionItemKind.Class,
                        insertText: table.name,
                        documentation: `${table.columns.length} columns`,
                        sortText: `20_table_${table.schema}_${table.name}`,
                        range,
                    });
                });
            }

            if (scope === 'COLUMN' || scope === 'DEFAULT') {
                referencedTables.forEach((table) => {
                    table.columns.forEach((column) => {
                        suggestions.push({
                            label: { label: column.name, description: column.type },
                            kind: monacoInstance.languages.CompletionItemKind.Field,
                            insertText: column.name,
                            documentation: column.nullable ? 'Nullable' : 'Required',
                            sortText: `30_column_${table.name}_${column.name}`,
                            range,
                        });
                    });
                });
            }

            if (scope === 'TABLE_DOT') {
                const qualifier = normalizeIdentifier(textBeforeCursor.match(/([\w"]+)\.$/i)?.[1] ?? '');
                const tableName = aliases.get(qualifier) ?? qualifier;
                const table = metadata.tables.find((candidate) => candidate.name.toLowerCase() === tableName);

                table?.columns.forEach((column) => {
                    suggestions.push({
                        label: { label: column.name, description: column.type },
                        kind: monacoInstance.languages.CompletionItemKind.Field,
                        insertText: column.name,
                        documentation: column.nullable ? 'Nullable' : 'Required',
                        sortText: `00_column_${column.name}`,
                        range,
                    });
                });
            }

            if (scope === 'COLUMN') {
                metadata.tables.forEach((table) => {
                    suggestions.push({
                        label: {
                            label: `${table.name}.*`,
                            description: 'Column Projection',
                        },
                        kind: monacoInstance.languages.CompletionItemKind.Module,
                        insertText: `${table.name}.*`,
                        sortText: `35_table_star_${table.name}`,
                        range,
                    });
                });
            }

            return { suggestions, incomplete: false };
        },
    });
